/**
 * Resolves cashier identifiers to display names by reading the client's profiles.
 * Returns two maps:
 *  - byNo:     cashier_no (integer) → display name  (legacy, HDM-based)
 *  - byUserId: user UUID             → display name  (preferred, per-person)
 *
 * Callers should prefer byUserId when cashier_user_id is available on the row.
 */
import { useEffect, useState } from 'react';
import { supabase } from './supabase.js';

export interface CashierNames {
  byNo: Record<number, string>;
  byUserId: Record<string, string>;
  /** Full list for dropdowns — includes the cashier_no needed to update a shift. */
  list: { id: string; cashierNo: number | null; name: string }[];
  /** Resolve: prefer user_id, fall back to cashier_no */
  resolve: (userId: string | null | undefined, cashierNo: number) => string;
}

export function useCashierNames(clientId: string | null): CashierNames {
  const [byNo, setByNo] = useState<Record<number, string>>({});
  const [byUserId, setByUserId] = useState<Record<string, string>>({});
  const [list, setList] = useState<{ id: string; cashierNo: number | null; name: string }[]>([]);

  useEffect(() => {
    if (!supabase || !clientId) return;
    void supabase
      .from('profiles')
      .select('id, cashier_no, full_name, username, email')
      .eq('client_id', clientId)
      .then(({ data }) => {
        const no: Record<number, string> = {};
        const uid: Record<string, string> = {};
        const lst: { id: string; cashierNo: number | null; name: string }[] = [];
        (data ?? []).forEach((p) => {
          const name =
            p.full_name?.trim() ||
            p.username?.trim() ||
            p.email?.trim() ||
            (p.cashier_no != null ? `#${p.cashier_no}` : '');
          if (!name) return;
          if (p.cashier_no != null) no[p.cashier_no] = name;
          if (p.id) uid[p.id] = name;
          lst.push({ id: p.id, cashierNo: p.cashier_no, name });
        });
        setByNo(no);
        setByUserId(uid);
        setList(lst);
      });
  }, [clientId]);

  function resolve(userId: string | null | undefined, cashierNo: number): string {
    if (userId && byUserId[userId]) return byUserId[userId]!;
    return byNo[cashierNo] ?? `#${cashierNo}`;
  }

  return { byNo, byUserId, list, resolve };
}
